import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { IconButton } from "@doculegis/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/AuthContext";
import { fetchDraftTemplates } from "@/lib/api/drafts";
import type { DraftTemplate } from "@/types/drafting";

interface DraftTemplateSelectProps {
  /** Selected draft template id. */
  value: string | null;
  onChange: (template: DraftTemplate | null) => void;
  hideLabel?: boolean;
}

export function DraftTemplateSelect({ value, onChange, hideLabel = false }: DraftTemplateSelectProps) {
  const canCreate = usePermission("draftTemplate", "create");
  const [templates, setTemplates] = useState<DraftTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const rows = await fetchDraftTemplates();
        if (!cancelled) setTemplates(rows);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = templates.find((t) => t.id === value) ?? null;

  return (
    <div>
      {hideLabel ? null : (
        <label className="mb-2 block text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
          Agreement template
        </label>
      )}
      <div className="flex gap-2">
        <Select
          value={value ?? ""}
          onValueChange={(id) => {
            const template = templates.find((row) => row.id === id) ?? null;
            onChange(template);
          }}
          disabled={isLoading || templates.length === 0}
        >
          <SelectTrigger className="min-w-0 flex-1">
            <SelectValue
              placeholder={isLoading ? "Loading templates…" : "Select agreement template"}
            />
          </SelectTrigger>
          <SelectContent>
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canCreate ? (
          <IconButton
            type="button"
            size="lg"
            className="shrink-0"
            asChild
            title="Add template"
            aria-label="Add template"
          >
            <Link to="/drafting/templates">
              <Plus className="h-4 w-4" />
            </Link>
          </IconButton>
        ) : null}
      </div>
      {selected?.blurb ? (
        <p className="mt-2 text-xs text-[var(--lm-muted)] dark:text-slate-500">{selected.blurb}</p>
      ) : null}
    </div>
  );
}
